import { Injectable } from '@angular/core';
import {
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { RegexEnum } from '../../enums/regex/regex.enum';

const NATIONAL_NUMBER_LENGTH = 11;
const CHECKSUM_START_INDEX = 9;
const MODULO = 97;

const isValidChecksum = (digits: number, checksum: number) =>
    MODULO - (digits % MODULO) === checksum;

export const belgianNationalNumberValidator = ((
    control: AbstractControl<string>,
): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
        return null;
    }

    if (value.length < NATIONAL_NUMBER_LENGTH) {
        return { minlength: true };
    }

    if (value.length > NATIONAL_NUMBER_LENGTH) {
        return { maxlength: true };
    }

    if (!RegexEnum.DIGITS_ONLY.test(value)) {
        return { pattern: value };
    }

    const digits = value.substring(0, CHECKSUM_START_INDEX);
    const checksum = Number.parseInt(
        value.substring(CHECKSUM_START_INDEX, NATIONAL_NUMBER_LENGTH),
    );

    const isBornBefore2000 = isValidChecksum(Number.parseInt(digits), checksum);
    const isBornFrom2000 = isValidChecksum(
        Number.parseInt(`2${digits}`),
        checksum,
    );

    const isValidNationalNumber = isBornBefore2000 || isBornFrom2000;
    if (isValidNationalNumber) {
        return null;
    }
    return { pattern: value };
}) satisfies ValidatorFn;

/**
 * Shared validators for common form fields (email, firstname, phone, address, etc.).
 * Used by both backoffice and fines-portal to keep validation rules consistent.
 */
@Injectable({
    providedIn: 'root',
})
export class FieldValidatorService {
    /** Required short text (e.g. firstname), max 40 chars */
    requiredShortText(): ValidatorFn[] {
        return [Validators.required, Validators.maxLength(40)];
    }

    /** Required medium text (e.g. lastname), max 100 chars */
    requiredMediumText(): ValidatorFn[] {
        return [Validators.required, Validators.maxLength(100)];
    }

    /** Required long name (e.g. company name), max 140 chars */
    requiredLongName(): ValidatorFn[] {
        return [Validators.required, Validators.maxLength(140)];
    }

    /** Optional email */
    optionalEmail(): ValidatorFn[] {
        return [Validators.email];
    }

    /** Required email */
    requiredEmail(): ValidatorFn[] {
        return [Validators.required, Validators.email];
    }

    /** Optional phone */
    optionalPhone(): ValidatorFn[] {
        return [Validators.pattern(RegexEnum.PHONE_NUMBER)];
    }

    /** Required phone */
    requiredPhone(): ValidatorFn[] {
        return [
            Validators.required,
            Validators.pattern(RegexEnum.PHONE_NUMBER),
        ];
    }

    /** National number, 11 digits — length and digits only, no checksum */
    nationalNumber(): ValidatorFn[] {
        return [
            Validators.minLength(11),
            Validators.maxLength(11),
            Validators.pattern(RegexEnum.DIGITS_ONLY),
        ];
    }

    /**
     * Belgian national registry number (RRN), 11 digits **with** the modulo-97
     * checksum. Prefer this over {@link nationalNumber} wherever the number
     * must be a genuine RRN rather than merely well-shaped.
     */
    belgianNationalNumber(): ValidatorFn[] {
        return [belgianNationalNumberValidator];
    }

    /** Company number (Belgian format) */
    companyNumber(): ValidatorFn[] {
        return [Validators.pattern(RegexEnum.COMPANY)];
    }

    /** Optional box number, max 6 chars */
    optionalBoxNumber(): ValidatorFn[] {
        return [Validators.maxLength(6)];
    }

    /** Optional house number */
    optionalHouseNumber(): ValidatorFn[] {
        return [
            Validators.maxLength(20),
            Validators.pattern(RegexEnum.HOUSE_NUMBER),
        ];
    }

    /** Required street, max 200 chars */
    requiredStreet(): ValidatorFn[] {
        return [Validators.required, Validators.maxLength(200)];
    }

    /** Required house number */
    requiredHouseNumber(): ValidatorFn[] {
        return [
            Validators.required,
            Validators.maxLength(20),
            Validators.pattern(RegexEnum.HOUSE_NUMBER),
        ];
    }

    /** Required postal code */
    requiredPostalCode(): ValidatorFn[] {
        return [
            Validators.required,
            Validators.maxLength(20),
            Validators.pattern(RegexEnum.POSTAL_CODE),
        ];
    }

    /** Required city, max 100 chars */
    requiredCity(): ValidatorFn[] {
        return [Validators.required, Validators.maxLength(100)];
    }
}
